import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { ROUTES } from "../../utils/constants";
import { fetchPublicBrandingDeduped } from "../../modules/platform/api/publicBrandingSingleFlight";
import {
  platformBrandingFallback,
  type EffectiveBranding,
} from "../../modules/platform/api/branding.types";
import { getPublicTenantIntent } from "../../utils/publicTenantHandoff";

export interface AuthSplitLayoutProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  showBackButton?: boolean;
  tenantId?: string;
  /** Login uses compact inner padding; other auth pages use the default shell. */
  panel?: "login" | "card";
}

const BG_SRC = "/images/tkm_land_section5.jpeg";
const MCE_LOGO_SRC = "/images/mce-logo.png";
const MALAY_CLASSROOM_LEARNING_SRC = "/images/malay-classroom-learning.png";

export const AuthSplitLayout: React.FC<AuthSplitLayoutProps> = ({
  children,
  title,
  subtitle,
  showBackButton = true,
  tenantId,
  panel = "card",
}) => {
  const navigate = useNavigate();
  const isLoginPanel = panel === "login";
  const [branding, setBranding] = useState<EffectiveBranding>(() => platformBrandingFallback());

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await fetchPublicBrandingDeduped(tenantId);
        if (!cancelled) setBranding(data);
      } catch {
        if (!cancelled) setBranding(platformBrandingFallback());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const publicIntent = getPublicTenantIntent();
  const isMalayTenant = Boolean(publicIntent?.isMalay);

  const displayNameTa = branding.display_name_ta;

  const bgImage = isMalayTenant ? MALAY_CLASSROOM_LEARNING_SRC : BG_SRC;

  return (
    <div className="relative min-h-screen overflow-hidden bg-black">
      {/* Full-Cover Background Image (Identical layout structure for both Tamil & Malay schools) */}
      <img
        src={bgImage}
        alt=""
        aria-hidden="true"
        className="pointer-events-none select-none absolute inset-0 h-full w-full object-cover object-center blur-2xl opacity-65"
      />
      <img
        src={bgImage}
        alt=""
        aria-hidden="true"
        className="pointer-events-none select-none absolute inset-0 h-full w-full object-cover object-center"
      />

      {/* Top Banner Header */}
      <div className="hidden lg:block absolute top-0 left-1/2 right-0 z-20">
        <div className="relative w-full bg-black/25 backdrop-blur-xl shadow-[0_14px_34px_rgba(0,0,0,0.38)]">
          <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-white/18 via-transparent to-transparent" />

          <div className="relative flex items-center h-20 px-7">
            <div className="w-full text-center flex items-center justify-center gap-3">
              {isMalayTenant ? (
                <>
                  <img src={MCE_LOGO_SRC} alt="MCE Logo" className="h-8 w-auto bg-white rounded-lg p-0.5 shadow-sm" />
                  <span className="text-white text-xl font-extrabold tracking-wide drop-shadow-[0_10px_22px_rgba(0,0,0,0.7)]">
                    Marshall Cavendish Education
                  </span>
                </>
              ) : (
                <span className="text-white tamil-text text-xl font-extrabold tracking-wide drop-shadow-[0_10px_22px_rgba(0,0,0,0.7)]">
                  {displayNameTa}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 min-h-screen">
        {/* Left Form Column */}
        <div
          data-testid="auth-form-column"
          className="flex flex-col min-h-screen lg:min-h-0 overflow-y-auto bg-app-surface/90 dark:bg-gray-900/85 backdrop-blur-xl"
        >
          <div className="flex flex-1 flex-col items-center justify-center p-6 sm:p-8 lg:p-10">
            <div className="w-full max-w-md space-y-8">
              {showBackButton ? (
                <button
                  type="button"
                  onClick={() => navigate(isMalayTenant ? "/ms" : ROUTES.LOGIN)}
                  className="flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors -ml-1"
                >
                  <ArrowLeft className="h-4 w-4" aria-hidden />
                  Home
                </button>
              ) : null}

              {(title || subtitle) && (
                <div>
                  {title ? (
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{title}</h1>
                  ) : null}
                  {subtitle ? (
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{subtitle}</p>
                  ) : null}
                </div>
              )}

              {isLoginPanel ? (
                <div className="space-y-6 p-4 sm:p-6 lg:p-7 max-w-md mx-auto">{children}</div>
              ) : (
                children
              )}
            </div>
          </div>
        </div>

        {/* Right Visual Column */}
        <div className="hidden lg:block" aria-hidden="true" />
      </div>
    </div>
  );
};
